<script setup lang="ts" generic="TRow">
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import ButtonV2 from '../ButtonV2/ButtonV2.vue';
import DropdownMenu from '../DropdownMenu/DropdownMenu.vue';
import TTooltip from '../TTooltip.vue';
import type { DataTableColumn } from './data-table';
import { DateTime } from 'luxon';
import { getUserLanguage } from '@teachino/shared-i18n';

const props = withDefaults(
  defineProps<{
    columns: DataTableColumn<TRow>[];
    rows: TRow[];
    /** Provide a unique key for each row — used as the `v-for` key. */
    rowKey: (row: TRow) => string | number;
    /** Message shown when `rows` is empty. */
    emptyText?: string;
    /** Optional classes applied to the empty-state message. */
    emptyTextClass?: string;
    /** Optional classes applied per row (e.g. highlight selected state). */
    rowClass?: (row: TRow) => string | undefined;
    /** When false, rows do not change background on hover. Defaults to true. */
    highlightRowsOnHover?: boolean;
    /** Keeps the header row visible while vertically scrolling the table. */
    stickyHeader?: boolean;
    /** Keeps the first column visible while horizontally scrolling the table. */
    stickyFirstColumn?: boolean;
    /** Optional classes for the scroll container (e.g. max height). */
    scrollContainerClass?: string;
    /** Renders a summary row using named slots `footer-{column key}`. */
    showFooter?: boolean;
  }>(),
  {
    emptyTextClass: '',
    highlightRowsOnHover: true,
    stickyHeader: false,
    stickyFirstColumn: false,
    showFooter: false,
  },
);

defineSlots<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Partial<Record<`cell-${string}`, (slotProps: { row: TRow }) => any>> &
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Partial<Record<`header-${string}`, () => any>> & {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: `footer-${string}`]: (() => any) | undefined;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      empty?: () => any;
    }
>();

const getColumnCellPaddingClass = (col: DataTableColumn<TRow>): string => {
  if (col.type === 'custom' && col.cellClass) {
    return col.cellClass;
  }

  return 'px-4 py-3';
};

const getColumnAlignClass = (
  col: DataTableColumn<TRow>,
): string | undefined => {
  if (col.type === 'custom' && col.align === 'right') {
    return 'text-right';
  }

  if (col.type === 'custom' && col.align === 'center') {
    return 'text-center';
  }

  if (col.type === 'context-menu') {
    return 'text-right';
  }

  return undefined;
};

const getColumnSeparatorClass = (
  col: DataTableColumn<TRow>,
): string | undefined => {
  if (col.type === 'custom' && col.separatorBefore) {
    return 'border-l border-slate-100';
  }

  return undefined;
};

const getColumnFitContentClass = (
  col: DataTableColumn<TRow>,
): string | undefined => {
  if (
    (col.type === 'custom' || col.type === 'context-menu') &&
    col.fitContent
  ) {
    return 'w-0 whitespace-nowrap';
  }

  return undefined;
};

const isScrollable = (): boolean =>
  props.stickyHeader || props.stickyFirstColumn;

const getStickyCellClass = (
  col: DataTableColumn<TRow>,
  colIndex: number,
  isHeader: boolean,
): string | undefined => {
  const classes: string[] = [];

  if (props.stickyHeader && isHeader) {
    classes.push('sticky top-0 z-20 bg-slate-50');
  }

  if (props.stickyFirstColumn && colIndex === 0) {
    const isFitContent = col.type === 'custom' && col.fitContent;

    classes.push('sticky left-0 shadow-[2px_0_4px_-2px_rgba(15,23,42,0.08)]');

    if (!isFitContent) {
      classes.push('min-w-48');
    }

    if (isHeader) {
      classes.push('z-30 bg-slate-50');
    } else {
      classes.push('z-20 bg-white');

      if (props.highlightRowsOnHover) {
        classes.push('group-hover:bg-slate-50');
      }
    }
  }

  if (
    props.stickyHeader &&
    props.stickyFirstColumn &&
    colIndex === 0 &&
    isHeader
  ) {
    classes.push('z-30');
  }

  return classes.length > 0 ? classes.join(' ') : undefined;
};

const getHeaderCellBorderClass = (): string | undefined =>
  props.stickyFirstColumn ? 'border-b border-slate-200' : undefined;

const getBodyCellBorderClass = (): string | undefined =>
  props.stickyFirstColumn ? 'border-b border-slate-100' : undefined;

const getFooterStickyCellClass = (colIndex: number): string | undefined =>
  props.stickyFirstColumn && colIndex === 0
    ? 'sticky left-0 z-20 bg-slate-50 shadow-[2px_0_4px_-2px_rgba(15,23,42,0.08)]'
    : undefined;

const getColumnKey = (col: DataTableColumn<TRow>, index: number): string => {
  if (col.type === 'custom') return `custom-${col.key}`;
  if (col.type === 'context-menu') return `context-menu-${String(index)}`;
  if (col.type === 'actions') return `actions-${String(index)}`;
  return `col-${String(index)}`;
};

const formatDateValue = (
  value: Date | string | null | undefined,
): string | null => {
  if (value == null) return null;

  const locale = getUserLanguage();

  if (value instanceof Date) {
    return DateTime.fromJSDate(value)
      .setLocale(locale)
      .toLocaleString(DateTime.DATETIME_MED);
  }

  const parsedDate = DateTime.fromISO(value).setLocale(locale);
  if (parsedDate.isValid) {
    return parsedDate.toLocaleString(DateTime.DATETIME_MED);
  }

  return value;
};
</script>

<template>
  <div class="overflow-hidden rounded-lg border border-slate-200 bg-white">
    <div
      :class="[
        isScrollable() ? 'overflow-auto' : 'overflow-x-auto',
        props.scrollContainerClass,
      ]"
    >
      <table
        class="w-full min-w-max text-left text-sm"
        :class="props.stickyFirstColumn && 'border-separate border-spacing-0'"
      >
        <thead>
          <tr
            class="bg-slate-50"
            :class="!props.stickyFirstColumn && 'border-b border-slate-200'"
          >
            <th
              v-for="(col, colIndex) in props.columns"
              :key="getColumnKey(col, colIndex)"
              class="font-semibold text-slate-700"
              :class="[
                getColumnCellPaddingClass(col),
                col.type === 'context-menu' && !col.fitContent && 'w-12',
                getColumnAlignClass(col),
                getColumnSeparatorClass(col),
                getColumnFitContentClass(col),
                getHeaderCellBorderClass(),
                getStickyCellClass(col, colIndex, true),
              ]"
              :style="
                col.type === 'custom' && col.minWidth
                  ? { minWidth: col.minWidth }
                  : undefined
              "
            >
              <template
                v-if="
                  col.type === 'custom' && $slots[`header-${col.key}` as const]
                "
              >
                <div
                  class="flex w-full"
                  :class="{
                    'justify-center': col.align === 'center',
                    'justify-end': col.align === 'right',
                  }"
                >
                  <slot :name="`header-${col.key}`" />
                </div>
              </template>
              <template v-else-if="col.type === 'custom' && col.headerTooltip">
                <TTooltip
                  :content="col.headerTooltip"
                  placement="top"
                  :interactive="false"
                >
                  <span class="block w-full cursor-default text-center">{{
                    col.label
                  }}</span>
                </TTooltip>
              </template>
              <template v-else>
                {{ 'label' in col ? col.label : '' }}
              </template>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="row in props.rows"
            :key="props.rowKey(row)"
            class="group last:border-0"
            :class="[
              !props.stickyFirstColumn && 'border-b border-slate-100',
              props.highlightRowsOnHover && 'hover:bg-slate-50',
              props.rowClass?.(row),
            ]"
          >
            <td
              v-for="(col, colIndex) in props.columns"
              :key="getColumnKey(col, colIndex)"
              :class="[
                getColumnCellPaddingClass(col),
                getColumnAlignClass(col),
                getColumnSeparatorClass(col),
                getColumnFitContentClass(col),
                getBodyCellBorderClass(),
                getStickyCellClass(col, colIndex, false),
              ]"
              :style="
                col.type === 'custom' && col.minWidth
                  ? { minWidth: col.minWidth }
                  : undefined
              "
            >
              <!-- Text column -->
              <span
                v-if="col.type === 'text'"
                :class="[
                  col.mono
                    ? 'font-mono text-slate-500'
                    : 'font-medium text-slate-900',
                ]"
              >
                {{ col.accessor(row) }}
              </span>

              <!-- Date column -->
              <span v-else-if="col.type === 'date'" class="text-slate-500">
                {{ formatDateValue(col.accessor(row)) ?? col.emptyText ?? '' }}
              </span>

              <!-- Actions column -->
              <div
                v-else-if="col.type === 'actions'"
                class="flex items-center gap-2"
              >
                <ButtonV2
                  v-for="btn in col.buttons"
                  :key="btn.label"
                  size="sm"
                  :icon="btn.icon"
                  :label="btn.label"
                  :variant="btn.variant ?? 'neutral'"
                  @click="btn.onClick(row)"
                />
              </div>

              <!-- Context-menu column -->
              <DropdownMenu
                v-else-if="col.type === 'context-menu'"
                :items="col.items(row)"
                :placement="col.placement ?? 'bottom-end'"
                :trigger-variant="col.variant ?? 'neutral'"
              />

              <!-- Custom column — rendered via named slot `#cell-{key}` -->
              <div
                v-else-if="col.type === 'custom' && col.align === 'right'"
                class="flex justify-end"
              >
                <slot :name="`cell-${col.key}`" :row="row" />
              </div>
              <div
                v-else-if="col.type === 'custom' && col.align === 'center'"
                class="flex justify-center"
              >
                <slot :name="`cell-${col.key}`" :row="row" />
              </div>
              <slot
                v-else-if="col.type === 'custom'"
                :name="`cell-${col.key}`"
                :row="row"
              />
            </td>
          </tr>
        </tbody>
        <tfoot v-if="props.showFooter && props.rows.length > 0">
          <tr class="bg-slate-50 font-semibold text-slate-700">
            <td
              v-for="(col, colIndex) in props.columns"
              :key="getColumnKey(col, colIndex)"
              class="border-t border-slate-200"
              :class="[
                getColumnCellPaddingClass(col),
                getColumnAlignClass(col),
                getColumnSeparatorClass(col),
                getColumnFitContentClass(col),
                getFooterStickyCellClass(colIndex),
              ]"
              :style="
                col.type === 'custom' && col.minWidth
                  ? { minWidth: col.minWidth }
                  : undefined
              "
            >
              <slot v-if="col.type === 'custom'" :name="`footer-${col.key}`" />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Empty state -->
    <div
      v-if="props.rows.length === 0"
      class="flex flex-col items-center justify-center gap-3 py-12 text-slate-400"
    >
      <HugeIcon icon="list_bullet" :size="32" />
      <p class="text-sm" :class="props.emptyTextClass">
        {{ props.emptyText ?? 'Keine Einträge' }}
      </p>
      <div v-if="$slots.empty" class="mt-3">
        <slot name="empty" />
      </div>
    </div>
  </div>
</template>
